import _ from 'lodash';

import React, { useState, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Typography, Checkbox, Grid, IconButton } from '@material-ui/core';
import { ENG, SHOOTING_STEPS, COLOR, SERVICE_OFFER_CATEGORY_ICON } from 'config/constants';
import { ServiceOffer, Shooting } from 'models';
import { ShootingContext } from 'contexts';
import { images } from 'assets/images';
import SaveRoundedIcon from '@material-ui/icons/SaveRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import { useLanguage } from 'hooks';
import { HvGoogleMap } from 'components';
import { checkIfCovered } from 'utils';

const useStyles = makeStyles({
  label: {
    color: '#818D99',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  saveContent: {
    display: 'flex',
    backgroundColor: COLOR.LIGHT_BLUE,
    borderRadius: 8,
    padding: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  column: {
    flexDirection: 'column',
  },
  boldText: {
    fontWeight: 'bold',
    color: COLOR.WHITE,
    fontSize: 18,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  specialOfferContent: {
    borderRadius: 8,
    backgroundColor: COLOR.LIGHT_BLUE,
    padding: 10,
    marginTop: 10,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
  },
  disabledSpecialOfferContent: {
    borderRadius: 8,
    backgroundColor: COLOR.LIGHT_GREY,
    padding: 10,
    marginTop: 10,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    color: COLOR.WHITE,
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: COLOR.WHITE,
    },
  },
  alertContent: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLOR.DARK_GREY,
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
    padding: 10,
    marginTop: 5,
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    width: 35,
    marginRight: 10,
  },
  warning: {
    color: COLOR.DARK_GREY,
    marginTop: 5,
    fontSize: 14,
  },
  saveButton: {
    color: COLOR.WHITE,
    backgroundColor: COLOR.LIGHT_BLUE,
    '@media (max-width: 767px)': {
      fontSize: 10,
      color: COLOR.LIGHT_BLUE,
      backgroundColor: COLOR.WHITE,
    },
  },
  nextButton: {
    color: COLOR.WHITE,
    '@media (max-width: 767px)': {
      fontSize: 9,
      width: '100%',
    },
  },
  discountedPrice: {
    marginLeft: 10,
  },
  buttons: {
    '@media (max-width: 767px)': {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  backButton: {
    backgroundColor: COLOR.WHITE,
    color: COLOR.LIGHT_BLUE,
    borderColor: COLOR.LIGHT_BLUE,
    marginRight: 20,
    '@media (max-width: 767px)': {
      fontSize: 10,
      marginRight: 5,
    },
  },
  addressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
  },
  buttonText: {
    '@media (max-width: 767px)': {
      display: 'none',
    },
  },
});

interface Props {
  setActiveStep: (_: number) => void;
  onCancel: () => void;
}

function ReviewStep({ setActiveStep, onCancel }: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const language = useLanguage();

  const {
    listing,
    shooting,
    updateShootingFields,
    serviceOffers,
    distanceFee,
    saveShooting,
    is_same_day_delivery_checked,
    setIsSameDayDeliveryChecked,
  } = useContext(ShootingContext);
  const [is_blue_hour_selected, setIsBlueHourSelected] = useState(false);
  const {
    service_offers, // selected service offers
    shootingPackage,
    totalCost,
    gstCost,
    qstCost,
    totalCostWithTax,
    potentialSaving,
  } = shooting;
  const isDraftMode = shooting.state === Shooting.STATE_ENUM.draft;

  const isEnglish = language === ENG;
  const fmtOnPlatform = intl.formatMessage({ id: 'shooting.detail.onPlatform' });
  const fmtSaveMoney = intl.formatMessage({ id: 'shooting.detail.saveMoney' });
  const fmtSameDayDelivery = intl.formatMessage({ id: 'services.sameDayServiceOffer' });
  const fmtDisableSameDayDelivery = intl.formatMessage({ id: 'services.disableSameDayDelivery' });
  const fmtSaveDraft = intl.formatMessage({ id: 'shooting.create.buttons.saveDraft' });
  const fmtSave = intl.formatMessage({ id: 'shooting.create.buttons.save' });

  let totalServicesPrice = 0;
  useEffect(() => {
    // check if blue hour is selected
    const blueHourServiceOffer = service_offers.find((m: ServiceOffer) => {
      return m.serviceOffer.blue_hour;
    });
    if (blueHourServiceOffer) {
      setIsBlueHourSelected(true);
    } else {
      setIsBlueHourSelected(false);
    }
  }, [service_offers]);
  service_offers.forEach((m: ServiceOffer) => {
    const { type_value_map, serviceOffer } = m;
    const { value, type } = type_value_map;
    const { price, increment_by } = serviceOffer;

    const isNumberType = type === ServiceOffer.TYPE.NUMBER;
    totalServicesPrice += isNumberType ? price * (value / increment_by) : price;
  });
  totalServicesPrice = _.round(totalServicesPrice, 2);

  const onPreviousStep = () => {
    setActiveStep(SHOOTING_STEPS.CLIENT_INFO);
  };

  const onNextStep = () => {
    setActiveStep(SHOOTING_STEPS.SELECT_DATETIME);
  };
  const sameDayServiceOffer = serviceOffers.find((m: ServiceOffer) => {
    return m.serviceOffer.category.icon_name === SERVICE_OFFER_CATEGORY_ICON.SAME_DAY_DELIVERY;
  });

  const handleCheckSpecialServiceOffer = () => {
    setIsSameDayDeliveryChecked(!is_same_day_delivery_checked);
    const selectedServiceOffers = _.clone(service_offers);
    const index = _.findIndex(service_offers, (m: ServiceOffer) => {
      return sameDayServiceOffer.serviceOffer.id === m.serviceOffer.id;
    });
    if (index !== -1) {
      selectedServiceOffers.splice(index, 1);
    } else {
      selectedServiceOffers.push(sameDayServiceOffer);
    }
    updateShootingFields({ service_offers: selectedServiceOffers }, true);
  };

  const getPrice = (type: number, value: number, increment_by: number, price: number) => {
    if (type === ServiceOffer.TYPE.NUMBER) {
      return `$${price}X${value / increment_by}`;
    }
    return `$${price}`;
  };
  return (
    <>
      <Box className='ScrollView'>
        <HvGoogleMap lat={Number(listing.lat)} lng={Number(listing.lng)} />
        {!sameDayServiceOffer || true ? null : (
          <Box
            onClick={() => {
              return handleCheckSpecialServiceOffer();
            }}
          >
            <Box
              className={
                is_blue_hour_selected
                  ? styles.disabledSpecialOfferContent
                  : styles.specialOfferContent
              }
            >
              <Box className={styles.row}>
                <img
                  src={is_blue_hour_selected ? images.fast_delivery_blue : images.fast_delivery}
                  style={{ width: 'auto', height: 30, marginRight: 10 }}
                  alt='fast delivery icon'
                />
                <Typography style={{ color: is_blue_hour_selected ? COLOR.BLACK : COLOR.WHITE }}>
                  {fmtSameDayDelivery}
                </Typography>
              </Box>
              <Checkbox
                className={styles.checkbox}
                checked={is_same_day_delivery_checked}
                disabled={is_blue_hour_selected}
              />
            </Box>
            {!is_blue_hour_selected ? null : (
              <Box className={styles.alertContent}>
                <InfoRoundedIcon color='secondary' className={styles.icon} />
                <Typography className={styles.warning}>{fmtDisableSameDayDelivery}</Typography>
              </Box>
            )}
          </Box>
        )}
        <Grid container spacing={2} className={styles.addressRow}>
          <Grid item md={5} sm={12}>
            <Typography className={styles.label}>
              {intl.formatMessage({ id: 'shooting.create.labels.address' })}
            </Typography>
            <Typography>{listing.fullAddress}</Typography>
          </Grid>
          <Grid item md={5} sm={12}>
            {potentialSaving > 0 ? (
              <Box className={styles.saveContent}>
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <img
                    src={images.discount}
                    style={{ width: 30, height: 30, marginRight: 10 }}
                    alt='saving icon'
                  />
                  <Box className={styles.column}>
                    <Typography className={styles.boldText}>
                      {`${fmtSaveMoney} $ ${potentialSaving}`}
                    </Typography>
                    <Typography style={{ color: COLOR.WHITE }}>{fmtOnPlatform}</Typography>
                  </Box>
                </Box>
                <Box
                  onClick={() => {
                    return window.open('/register', '_blank');
                  }}
                >
                  <ChevronRightRoundedIcon style={{ color: COLOR.WHITE }} />
                </Box>
              </Box>
            ) : null}
          </Grid>
        </Grid>

        {shootingPackage && shootingPackage.id ? (
          <Box className='FlexBox Vertical'>
            <Typography className={styles.label}>
              {intl.formatMessage({ id: 'shooting.create.labels.package' })}
            </Typography>
            <Box className='FlexBox SpaceBetween NoMargin'>
              <Box className='FlexBox Vertical'>
                <Typography>
                  {isEnglish ? shootingPackage.display_name_en : shootingPackage.display_name_fr}
                </Typography>
                {shootingPackage.hdr_photo_count && (
                  <Typography>{`${shootingPackage.hdr_photo_count} HDR photos`}</Typography>
                )}
              </Box>
              {shootingPackage.price && <Typography>{`$${shootingPackage.price}`}</Typography>}
            </Box>
          </Box>
        ) : null}

        {distanceFee && distanceFee.additional_km ? (
          <Box className='FlexBox Vertical'>
            <Typography className={styles.label}>
              {intl.formatMessage({ id: 'shooting.create.labels.extraDistanceFee' })}
            </Typography>
            <Box className='FlexBox SpaceBetween NoMargin'>
              <Typography>
                {`${distanceFee?.additional_km} km(s) ($${distanceFee?.price_per_km}/km)`}
              </Typography>
              <Typography>
                {`$${_.round(distanceFee?.additional_km * distanceFee?.price_per_km, 2)}`}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {service_offers.length ? (
          <Box className='FlexBox Vertical'>
            <Box className='FlexBox SpaceBetween NoMargin'>
              <Typography className={styles.label}>
                {intl.formatMessage({ id: 'shooting.create.labels.extraOptions' })}
              </Typography>
            </Box>
            {service_offers.map((so: ServiceOffer) => {
              if (so.is_child === true) {
                return null;
              }
              const { type_value_map, serviceOffer } = so;
              const { type, value } = type_value_map;
              const { id, display_name, display_name_fr, require_mls, price, increment_by } =
                serviceOffer;
              const isCovered = checkIfCovered(service_offers, so);
              const displayedPrice = getPrice(type, value, increment_by, price);
              return (
                <Box key={id} className='FlexBox SpaceBetween NoMargin'>
                  <Typography>
                    {isEnglish ? display_name : display_name_fr}
                    {require_mls && !!listing.mls ? `(MLS: ${listing.mls})` : ''}
                  </Typography>
                  <div className={styles.row}>
                    <Typography
                      style={{
                        textDecoration: isCovered ? 'line-through' : 'none',
                        textDecorationColor: isCovered ? COLOR.RED : COLOR.BLACK,
                        color: isCovered ? COLOR.DARK_GREY : COLOR.BLACK,
                      }}
                    >
                      {displayedPrice}
                    </Typography>
                    {isCovered ? (
                      <Typography className={styles.discountedPrice}>$ 0</Typography>
                    ) : null}
                  </div>
                </Box>
              );
            })}
          </Box>
        ) : null}
        <hr />

        <Box className='FlexBox AlignCenter SpaceBetween'>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.subTotal' })}</Typography>
          <Typography>{`$${totalCost}`}</Typography>
        </Box>
        <hr />

        <Box className='FlexBox AlignCenter SpaceBetween'>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.gst' })}</Typography>
          <Typography>{`$${gstCost}`}</Typography>
        </Box>

        <Box className='FlexBox AlignCenter SpaceBetween'>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.qst' })}</Typography>
          <Typography>{`$${qstCost}`}</Typography>
        </Box>
        <hr />

        <Box className='FlexBox AlignCenter SpaceBetween'>
          <Typography>{intl.formatMessage({ id: 'shooting.detail.total' })}</Typography>
          <Typography>{`$${totalCostWithTax}`}</Typography>
        </Box>
      </Box>

      <Box className='ButtonsBox'>
        <div className={styles.buttons}>
          <Button variant='contained' className='BackButton' onClick={onCancel}>
            {intl.formatMessage({ id: 'shooting.create.buttons.cancel' })}
          </Button>
          <Button className={styles.saveButton} onClick={saveShooting}>
            <SaveRoundedIcon />
            <Typography className={styles.buttonText}>
              {isDraftMode || !shooting.id ? fmtSaveDraft : fmtSave}
            </Typography>
          </Button>
        </div>
        <div className={styles.buttons}>
          <Button className={styles.backButton} variant='outlined' onClick={onPreviousStep}>
            <ChevronLeftRoundedIcon />
            {intl.formatMessage({ id: 'shooting.create.buttons.back' })}
          </Button>

          <Button
            variant='contained'
            color='secondary'
            className={styles.nextButton}
            onClick={onNextStep}
          >
            {intl.formatMessage({ id: 'shooting.create.buttons.selectDate' })}
          </Button>
        </div>
      </Box>
    </>
  );
}

export default ReviewStep;
